/**
 * Paginator — offset/limit pagination control (renamed + rethemed from the
 * retired legacy paginator). Composes the numbered page buttons
 * (`PaginationButtons`) with an optional page-size limiter (`PaginatorLimiter`)
 * and reports changes via `onPaginationChange`. Themed with `dp-paginator-*`
 * classes per the anchor design system.
 */
import {PaginationInfo} from "@devour/client";
import {ReactElement} from "react";
import {omit} from "lodash";
import PaginationButtons from "@/components/paginator/PaginationButtons";
import classNames from "classnames";
import PaginatorLimiter, {paginationLimiterOptions} from "@/components/paginator/PaginatorLimiter";

export type FrontendPagination = Pick<PaginationInfo, "offset" | "limit">;

/*
 * Use this for any api requests with pagination to have consistent defaults across apis. If there are api requests
 * that you specifically want to use different defaults for, then go right ahead.
 */
export const defaultFrontendPagination: FrontendPagination = {
    offset: 0,
    limit: paginationLimiterOptions?.[1]?.value as number,
};

export type PaginatorTheme = "dark" | "light";
interface Props extends PaginationInfo {
    onPaginationChange: (newPagination: FrontendPagination) => void;
    theme?: PaginatorTheme;
    showPaginatorLimit?: boolean;
}

function Paginator({
    onPaginationChange,
    theme = "light",
    showPaginatorLimit = false,
    limit,
    ...props
}: Props): ReactElement {
    function onChangeLimit(limit: number): void {
        onPaginationChange(omit({
            offset: 0,
            limit,
        }, "onPaginationChange"));
    }

    function onChangeOffset(offset: number): void {
        onPaginationChange(omit({
            limit: limit,
            offset,
        }, "onPaginationChange"));
    }

    return (
        <div
            className={classNames("dp-paginator", {
                "theme-dark": theme === "dark",
                "show-limiter": showPaginatorLimit,
            })}
        >
            {showPaginatorLimit &&
                <PaginatorLimiter
                    {...omit(props, "onPaginationChange")}
                    limit={limit}
                    onChangeLimit={onChangeLimit}
                />
            }

            <PaginationButtons
                {...omit(props, "onPaginationChange")}
                limit={limit}
                onChangeOffset={onChangeOffset}
            />
        </div>
    );

}

export default Paginator;